import React, { useState, useEffect } from 'react';
import { Brain, ArrowRight, CheckCircle, X } from 'lucide-react';
import { Button, Card, Spinner, Alert } from '../design-system';
import groundingService from '../../services/groundingService';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import analyticsService from '../../services/analyticsService';

/**
 * Thoughts and Facts Grounding Activity
 * Helps users examine catastrophic thoughts objectively
 */
const ThoughtsAndFacts = ({ assignment, onComplete, onExit }) => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const sessionStartTimeRef = React.useRef(null);
  
  const [responses, setResponses] = useState({
    thought: '',
    supporting: '',
    against: '',
    balanced: ''
  });

  const steps = [
    {
      title: "What's on your mind?",
      question: "Describe the thought or worry that's bothering you right now.",
      placeholder: "I'm worried that...",
      field: 'thought'
    },
    {
      title: "What's supporting this?",
      question: "What evidence or reasons make this thought seem true?",
      placeholder: "The reasons I think this might be true are...",
      field: 'supporting'
    },
    {
      title: "What's against this?",
      question: "What evidence or reasons suggest this thought might not be true?",
      placeholder: "The reasons this might not be true are...",
      field: 'against'
    },
    {
      title: "What's a balanced view?",
      question: "Based on both sides, what's a more balanced perspective?",
      placeholder: "A more balanced way to think about this is...",
      field: 'balanced'
    }
  ];

  // Auto-start the session when component mounts
  useEffect(() => {
    const startSession = async () => {
      try {
        const response = await groundingService.startSession(
          user,
          assignment.groundingId,
          {}
        );
        setSessionId(response.session.sessionId);
        // Track session start time for duration calculation
        sessionStartTimeRef.current = Date.now();
        
        // Track grounding session started event
        try {
          analyticsService.track('grounding_session_started', {
            sessionId: response.session.sessionId,
            groundingId: assignment.groundingId,
            techniqueType: assignment.techniqueType,
            isBreathing: assignment.techniqueType === 'breathing',
            isThoughtsFacts: assignment.techniqueType === 'thoughts-facts'
          });
        } catch (analyticsError) {
          // Don't break session start if analytics fails
          console.error('Analytics tracking error:', analyticsError);
        }
      } catch (error) {
        console.error('Error starting session:', error);
        toast.error('Failed to start grounding session');
      }
    };
    
    startSession();
  }, []);


  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    if (isCompleting || !sessionId) {
      return;
    }
    
    try {
      setIsCompleting(true);
      const startTime = sessionStartTimeRef.current || Date.now();
      const response = await groundingService.completeSession(user, sessionId, {
        cyclesCompleted: 1, // One complete exercise
        interruptions: 0,
        exerciseData: responses
      });
      
      // Calculate session duration (use response duration if available, otherwise calculate)
      const duration = response.session?.duration 
        ? Math.round(response.session.duration / 1000) // Convert from ms to seconds
        : Math.round((Date.now() - startTime) / 1000); // Fallback: calculate from start time
      
      // Track grounding session completed event
      try {
        analyticsService.track('grounding_session_completed', {
          sessionId: sessionId,
          groundingId: assignment.groundingId,
          techniqueType: assignment.techniqueType,
          isBreathing: assignment.techniqueType === 'breathing',
          isThoughtsFacts: assignment.techniqueType === 'thoughts-facts',
          cyclesCompleted: 1,
          duration: duration,
          interruptions: 0,
          hasExerciseData: !!responses.thought && !!responses.balanced
        });
      } catch (analyticsError) {
        // Don't break session completion if analytics fails
        console.error('Analytics tracking error:', analyticsError);
      }
      
      toast.success('Thoughts and Facts exercise completed!');
      onComplete();
    } catch (error) {
      console.error('Error completing session:', error);
      toast.error('Failed to complete exercise');
    } finally {
      setIsCompleting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setResponses(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const currentStepData = steps[currentStep];
  const canProceed = responses[currentStepData.field].trim().length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-6">
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <Brain className="h-6 w-6 text-purple-600" />
            </div>
          </div>
          <h3 className="text-xl font-semibold">Thoughts and Facts</h3>
          <p className="text-sm text-gray-600 mt-2">
            Step {currentStep + 1} of {steps.length}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-purple-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Current Step Content */}
        <div className="mb-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-2">
            {currentStepData.title}
          </h4>
          <p className="text-gray-600 mb-4">
            {currentStepData.question}
          </p>
          
          <textarea
            value={responses[currentStepData.field]}
            onChange={(e) => handleInputChange(currentStepData.field, e.target.value)}
            placeholder={currentStepData.placeholder}
            className="w-full p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            rows={6}
            autoFocus
          />
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button 
            variant="outline" 
            onClick={currentStep === 0 ? (onExit || onComplete) : handleBack}
            disabled={isCompleting}
          >
            <X className="h-4 w-4 mr-2" />
            {currentStep === 0 ? 'Exit' : 'Back'}
          </Button>
          
          <Button 
            onClick={handleNext}
            disabled={!canProceed || isCompleting}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {currentStep === steps.length - 1 ? (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                {isCompleting ? 'Completing...' : 'Complete'}
              </>
            ) : (
              <>
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ThoughtsAndFacts;
